import { Button } from 'doctornow-design-system';

import ClickLogger from '@/components/common/ClickLogger';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import { cn } from '@/utils/style';

import { DOCTOR_FILTER_OPTIONS, EDoctorFilterType, MAX_FILTER_SELECTION } from '@/models/diagnosis/doctor-filter';

import MaterialIcon from '@/components/atom/MaterialIcon';
import theme from '@/styles/theme';
import DoctorFilterOptionButton from './DoctorFilterOptionButton';

interface Props {
  selectedFilters: EDoctorFilterType[];
  onToggleFilter: (filterType: EDoctorFilterType) => void;
  onNext: () => void;
}

const DoctorFilterSelectionStep = ({ selectedFilters, onToggleFilter, onNext }: Props) => {
  const selectedCount = selectedFilters.length;
  const isNextButtonDisabled = selectedCount === 0;
  const canSelectMore = selectedCount < MAX_FILTER_SELECTION;

  const isFilterSelected = (filterType: EDoctorFilterType) => {
    return selectedFilters.includes(filterType);
  };

  return (
    <div className='flex h-full flex-col pb-[160px]'>
      <div className='px-[24px] pt-[12px] pb-[8px]'>
        <p className='font-semibold text-[24px] text-gray-900 leading-[32px]'>어떤 의사를 선호하시나요?</p>
      </div>

      <div className='flex-1 overflow-y-auto px-[20px] py-[40px]'>
        <div className='grid grid-cols-2 gap-[16px]'>
          {DOCTOR_FILTER_OPTIONS.map((option) => {
            const isSelected = isFilterSelected(option.type);
            return (
              <ClickLogger
                key={option.type}
                params={{
                  eventName: EWebTreatmentRequestLog.ClickDoctorFilterOption,
                  payload: {
                    filterType: option.type,
                    isSelected: isSelected ? 'true' : 'false',
                  },
                }}
              >
                <DoctorFilterOptionButton
                  option={option}
                  isSelected={isSelected}
                  canSelectMore={canSelectMore}
                  onToggle={onToggleFilter}
                />
              </ClickLogger>
            );
          })}
        </div>
      </div>

      <div
        className={cn([
          'fixed right-0 bottom-0 left-0 z-50 mx-auto w-full max-w-[600px]',
          'desktop:absolute desktop:max-w-none',
        ])}
      >
        <div
          className={cn([
            'pointer-events-auto mx-auto flex w-full max-w-[600px] flex-col items-center justify-center bg-white px-[20px] pb-[16px]',
            'desktop:max-w-none',
          ])}
        >
          <div className='mb-[16px] flex items-center justify-center gap-[4px]'>
            {selectedCount > 0 ? (
              <>
                <MaterialIcon name='check_circle' size={14} color={theme.color.green600} filled />
                <p className='font-medium text-[#40c057] text-[13px] leading-[18px]'>{selectedCount}개 선택</p>
              </>
            ) : (
              <p className='font-medium text-[13px] text-gray-400 leading-[18px]'>0개 선택</p>
            )}
            <p className='font-medium text-[12px] text-gray-100 leading-[18px]'>/</p>
            <p className='font-medium text-[13px] text-gray-400 leading-[18px]'>최대 {MAX_FILTER_SELECTION}개</p>
          </div>
          <ClickLogger
            params={{
              eventName: EWebTreatmentRequestLog.ClickNextFromDoctorFilter,
              payload: {
                selectedCount: selectedCount,
              },
            }}
          >
            <Button buttonColor='primary' size='xl' disabled={isNextButtonDisabled} onClick={onNext} className='w-full'>
              다음
            </Button>
          </ClickLogger>
        </div>
      </div>
    </div>
  );
};

export default DoctorFilterSelectionStep;
